/**
 * Agent TypeScript types
 * 
 * Based on backend schema (api/src/agents/schemas.py)
 * 
 * Note: No 'slug' field - removed in v1.0.0-rc1-fix10
 * Note: 'access_config' renamed to 'settings' in v1.0.0-rc1-fix10
 */

// =============================================================================
// Agent Status
// =============================================================================

export type AgentStatus = 'draft' | 'active' | 'deprecated' | 'archived';

// =============================================================================
// Model Configuration
// =============================================================================

export interface ModelConfig {
  provider: string;
  model: string;
  temperature?: number;
  max_tokens?: number;
  deployment_name?: string;
  api_version?: string;
}

export interface ModelsConfig {
  main: ModelConfig;
  router?: ModelConfig;
  tools?: ModelConfig;
  summarizer?: ModelConfig;
  fallback?: ModelConfig;
}

// =============================================================================
// Prompts Configuration
// =============================================================================

export interface PromptConfig {
  system: string;
}

export interface PromptsConfig {
  main: PromptConfig;
  router?: PromptConfig;
  guardrails?: string[];
}

// =============================================================================
// Knowledge Configuration
// =============================================================================

export interface CollectionConfig {
  name: string;
  priority?: number;
  filters?: Record<string, unknown>;
}

export interface RetrievalConfig {
  top_k?: number;
  rerank_enabled?: boolean;
  score_threshold?: number;
  reference_budget_tokens?: number;
}

export interface KnowledgeConfig {
  collections?: CollectionConfig[];
  retrieval?: RetrievalConfig;
  taxonomy_preferences?: string[];
  taxonomy_boost?: number;
}

// =============================================================================
// Tools Configuration
// =============================================================================

export interface ToolConfig {
  server: string;
  operations: string[];
}

// =============================================================================
// Delegation Settings
// =============================================================================

export interface DelegationSettingsConfig {
  enabled?: boolean;
  can_be_delegated_to?: boolean;
  max_depth?: number;
  delegation_prompt?: string | null;
  max_response_tokens?: number;
  max_fan_out?: number;
}

// =============================================================================
// Agent Settings
// =============================================================================

export interface AgentSettings {
  max_context_messages?: number;
  context_compression_enabled?: boolean;
  feedback_enabled?: boolean;
  feedback_required?: boolean;
  log_conversations?: boolean;
  log_tool_calls?: boolean;
  delegation?: DelegationSettingsConfig;
  runner_policy?: {
    allowed_runner_ids?: string[];
  };
  /** Sprint 278 (ROUTE-PIPE-1/A — REQ-LLM-006 §8.1): overrides del pipeline
   *  (mismos bloques que `hive.settings`). Ausente = heredar de la capa
   *  anterior (hive ← tenant ← DR ← default). */
  routing?: PipelineSettingsBlocks['routing'];
  optimization?: PipelineSettingsBlocks['optimization'];
  loop?: PipelineSettingsBlocks['loop'];
  autonomy?: PipelineSettingsBlocks['autonomy'];
  timeouts?: PipelineSettingsBlocks['timeouts'];
}

/** Bloques anidados de la política del pipeline en `settings` (hive/agente). */
export interface PipelineSettingsBlocks {
  routing?: {
    enabled?: boolean | null;
    mode?: 'auto' | 'direct' | 'planned' | null;
    on_failure?: 'degrade_direct' | 'fail_closed' | 'skip_stage' | null;
    confidence_threshold?: number | null;
    timeout_ms?: number | null;
    budget_tokens?: number | null;
    planning_on_failure?: 'skip' | 'fail_closed' | null;
    grounding_check?: 'off' | 'warn' | 'enforce' | null;
    plan_review?: 'off' | 'high_risk' | 'always' | null;
    retrieval_gating_enabled?: boolean | null;
  } | null;
  optimization?: { enabled?: boolean | null } | null;
  loop?: { max_iterations?: number | null } | null;
  autonomy?: {
    frontier?: boolean | null;
    max_duration_minutes?: number | null;
    on_unavailable?: 'standard' | 'fail_closed' | null;
  } | null;
  timeouts?: {
    foreground_seconds?: number | null;
    delegation_sync_seconds?: number | null;
    delegation_background_seconds?: number | null;
    fanout_aggregation_seconds?: number | null;
  } | null;
}

// =============================================================================
// Agent Types
// =============================================================================

/**
 * Agent exposure mode (Sprint 219 — REQ-AGT-003, ADR-094).
 * `use_only` agents are platform-managed black boxes: the API returns the
 * restricted representation (identity only) and writes fail with 403.
 */
export type DeliveryMode = 'installable' | 'use_only';

export interface Agent {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  icon?: string;
  status: AgentStatus;
  version: string;
  /** Hive al que pertenece el agente (si aplica) — alimenta la herencia de
   *  skills en el tab Skills (ISS-563). */
  hive_id?: string | null;
  /** Decisiones explícitas de capacidades nativas (REQ-CAP-001/002, ADR-113).
   *  null = heredar; lista = pertenencia habilitada explícitamente. */
  capabilities?: string[] | null;
  /** Settings explícitos por capacidad ({"slug": {...}}), con validación
   *  fail-closed contra el `config_schema` (ISS-567: API de escritura activa). */
  capability_settings?: Record<string, Record<string, unknown>> | null;
  /** Presentación (REQ-AGT-009): perfil frontera activo; alimenta el selector
   *  del editor de rutinas y los indicadores. El perfil solo lo cambia un
   *  responsable frontera. */
  frontier_enabled?: boolean;
  models_config: ModelsConfig;
  prompts_config: PromptsConfig;
  knowledge_config: KnowledgeConfig;
  tools_config: ToolConfig[];
  settings: AgentSettings;
  delivery_mode?: DeliveryMode;
  created_by?: string | null;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
  activated_at?: string | null;
  deprecated_at?: string | null;
}

/**
 * Restricted representation of a `use_only` agent (never includes
 * models/prompts/tools/knowledge/skills_scope — projection at the API
 * boundary). `settings` carries only the runtime usage slice
 * (runner_policy.allowed_runner_ids — ISS-409).
 */
export interface RestrictedAgent {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  icon?: string;
  status: AgentStatus;
  version: string;
  delivery_mode: 'use_only';
  hive_id?: string | null;
  settings?: {
    runner_policy?: {
      allowed_runner_ids?: string[];
    };
    /**
     * Slice operativo de delegación del tenant (enmienda 14-Oct-2026).
     * Solo claves estructurales + `delegation_prompt` cuando el tenant lo
     * escribió (`_tenant_owned`); el prompt del publisher nunca viaja.
     */
    delegation?: {
      enabled?: boolean;
      can_be_delegated_to?: boolean;
      max_depth?: number;
      max_response_tokens?: number;
      max_fan_out?: number;
      delegation_prompt?: string;
    };
  };
  /**
   * Capacidades nativas con decisión explícita del tenant (slugs) +
   * settings escritos por el tenant (enmienda 14-Oct-2026, toggle).
   */
  capabilities?: string[] | null;
  capability_settings?: Record<string, Record<string, unknown>> | null;
  created_at: string;
  updated_at: string;
}

export function isRestrictedAgent(
  agent: Agent | RestrictedAgent,
): agent is RestrictedAgent {
  return agent.delivery_mode === 'use_only';
}

export interface AgentSummary {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  icon?: string;
  status: AgentStatus;
  version: string;
  delivery_mode?: DeliveryMode;
  /** Perfil frontera activo (REQ-AGT-009; campo de presentación del listado). */
  frontier_enabled?: boolean;
  hive_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentCreate {
  name: string;
  description?: string;
  icon?: string;
  models_config: ModelsConfig;
  prompts_config?: PromptsConfig;
  knowledge_config?: KnowledgeConfig;
  tools_config?: ToolConfig[];
  settings?: Partial<AgentSettings>;
}

export interface AgentUpdate {
  /** Decisión explícita de capacidades del agente (null = heredar). */
  capabilities?: string[] | null;
  /** Settings explícitos por capacidad (ISS-567; validados fail-closed en API). */
  capability_settings?: Record<string, Record<string, unknown>> | null;
  name?: string;
  description?: string;
  icon?: string;
  status?: AgentStatus;
  models_config?: Partial<ModelsConfig>;
  prompts_config?: Partial<PromptsConfig>;
  knowledge_config?: Partial<KnowledgeConfig>;
  tools_config?: ToolConfig[];
  settings?: Partial<AgentSettings>;
}

// =============================================================================
// Available Models (from API /api/v1/models/available)
// =============================================================================

/**
 * Model category for different agent use cases
 */
export type ModelCategory = 'main' | 'router' | 'tools' | 'embedding';

/**
 * Available model from the canonical model catalog
 * Response from GET /api/v1/models/available (catalog-driven, ADR-041).
 * Agents select catalog models; the serving provider is resolved by policy.
 */
export interface AvailableModel {
  id: string;
  provider_id?: string | null;
  provider_name?: string | null;
  model_id: string;
  catalog_slug?: string;
  display_name: string;
  category: ModelCategory;
  context_window: number;
  supports_vision: boolean;
  supports_tools: boolean;
  is_default: boolean;
  /** Provider-specific config (dimensions, etc.) — present for embedding models */
  deployment_config?: Record<string, unknown>;
}

/**
 * Agent model configuration for simple model selection
 * Used when selecting models by ID from available models
 */
export interface AgentModelSelection {
  main?: string;       // model ID from AvailableModel
  router?: string;     // optional
  tools?: string;      // optional
  fallback?: string;   // optional
}
